import { FolderOpen } from 'lucide-react';
import { ActionBar, Container, PageHeader, Section } from '@vnetwork/ui/layout';
import { Button, Card, Checkbox, Input, Skeleton, Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { JobCreateSourceSection } from '../../jobs/job-create/JobCreateSourceSection';
import { JobCreateOutputSection } from '../../jobs/job-create/JobCreateOutputSection';
import { JobCreateNotificationSection } from '../../jobs/job-create/JobCreateNotificationSection';
import { useWatchFolderCreateForm } from './use-watch-folder-create-form';
import type { OutputKey } from '../../jobs/job-create/use-job-create-form';
import styles from './WatchFolderCreate.module.css';

// ─── Watch-folder-specific output extension ──────────────────────────────────
// The shared JobCreateOutputSection has no FairPlay or DASH protectionTypes UI.
// Watch-folders add those via this local add-on block rendered beneath the
// shared output section.

interface WatchFolderOutputExtensionProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: { outputOptions: Record<string, any> };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  verimatrixes: any[];
  isOutputSelected: (k: OutputKey) => boolean;
  toggleHlsProtection: (
    field: 'noneEnabled' | 'aesEnabled' | 'verimatrixEnabled' | 'fairplayEnabled',
    checked: boolean,
  ) => void;
  toggleDashProtection: (type: 3 | 5 | 6, checked: boolean) => void;
}

function WatchFolderOutputExtension({
  form,
  isOutputSelected,
  toggleHlsProtection,
  toggleDashProtection,
}: WatchFolderOutputExtensionProps) {
  const hlsCfg = form.outputOptions.hls as Record<string, unknown>;
  const dashCfg = form.outputOptions.dash as Record<string, unknown>;
  const dashTypes = Array.isArray(dashCfg.protectionTypes)
    ? (dashCfg.protectionTypes as number[])
    : [3];

  const hlsActive = isOutputSelected('hls');
  const dashActive = isOutputSelected('dash');

  if (!hlsActive && !dashActive) return null;

  return (
    <Section title="Protection Extensions" style={{ marginBottom: 0 }}>
      <Card flat className={styles.extensionCard}>

        {/* HLS FairPlay — watch-folder only (legacy WatchFolderOutputSection:132) */}
        {hlsActive && (
          <div className={styles.extensionBlock}>
            <p className={styles.extensionLabel}>HLS — Additional Protection</p>
            <label className={styles.checkRow}>
              <Checkbox
                checked={Boolean(hlsCfg.fairplayEnabled)}
                onCheckedChange={(v) => toggleHlsProtection('fairplayEnabled', v === true)}
              />
              <span>
                <span className={styles.checkLabel}>FairPlay over Verimatrix Multi-Rights</span>
              </span>
            </label>
          </div>
        )}

        {/* DASH protectionTypes multi-select — watch-folder only (legacy WatchFolderOutputSection:155-165) */}
        {dashActive && (
          <div className={styles.extensionBlock}>
            <p className={styles.extensionLabel}>MPEG-DASH — Protection Types</p>
            <div className={styles.checkGrid}>
              <label className={styles.checkRow}>
                <Checkbox
                  checked={dashTypes.includes(3)}
                  onCheckedChange={(v) => toggleDashProtection(3, v === true)}
                />
                <span className={styles.checkLabel}>None</span>
              </label>
              <label className={styles.checkRow}>
                <Checkbox
                  checked={dashTypes.includes(5)}
                  onCheckedChange={(v) => toggleDashProtection(5, v === true)}
                />
                <span className={styles.checkLabel}>Widevine over Verimatrix Multi-Rights</span>
              </label>
              <label className={styles.checkRow}>
                <Checkbox
                  checked={dashTypes.includes(6)}
                  onCheckedChange={(v) => toggleDashProtection(6, v === true)}
                />
                <span className={styles.checkLabel}>PlayReady over Verimatrix Multi-Rights</span>
              </label>
            </div>
          </div>
        )}

      </Card>
    </Section>
  );
}

// ─── Watch-folder-specific distribution section ───────────────────────────────
// WatchFolders share a single target/storage across all output formats,
// then have per-format output path strings — simpler than the job distribution model.

const TARGET_OPTIONS = [
  { value: 'ftp', label: 'FTP' },
  { value: 'sftp', label: 'SFTP' },
  { value: 's3', label: 'AWS S3 / OSS' },
  { value: 'azure', label: 'Microsoft Azure' },
  { value: 'webdav', label: 'WebDAV' },
];

const FORMAT_LABELS: Record<OutputKey, string> = {
  hls: 'HLS',
  dash: 'MPEG-DASH',
  mss: 'MS-SSTR',
  mp4: 'MP4',
};

interface WatchFolderDistributionProps {
  target: string;
  storage: string;
  paths: Record<OutputKey, string>;
  selectedFormats: OutputKey[];
  uploadStorages: Record<string, unknown>[];
  onTargetChange: (v: string) => void;
  onStorageChange: (v: string) => void;
  onPathChange: (fmt: OutputKey, v: string) => void;
}

function WatchFolderDistributionSection({
  target,
  storage,
  paths,
  selectedFormats,
  uploadStorages,
  onTargetChange,
  onStorageChange,
  onPathChange,
}: WatchFolderDistributionProps) {
  return (
    <Section title="Distribution" style={{ marginBottom: 0 }}>
      <Card flat className={styles.distributionCard}>
        <div className={styles.distributionFields}>
          <Field label="Target" required>
            {(p) => (
              <Select value={target} onValueChange={onTargetChange}>
                <SelectTrigger size="sm" {...p}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TARGET_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>

          <Field label="Storage" required>
            {(p) => (
              uploadStorages.length === 0 ? (
                <p className={styles.noStorages}>No {target.toUpperCase()} storages configured.</p>
              ) : (
                <Select value={storage} onValueChange={onStorageChange}>
                  <SelectTrigger size="sm" {...p}><SelectValue placeholder="Select storage…" /></SelectTrigger>
                  <SelectContent>
                    {uploadStorages.map((s) => {
                      const val = String(s?.id ?? s?.name ?? '');
                      return (
                        <SelectItem key={val} value={val}>
                          {String(s?.name ?? val)}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              )
            )}
          </Field>
        </div>

        {selectedFormats.length === 0 ? (
          <p className={styles.noFormats}>Select at least one output format to configure paths.</p>
        ) : (
          <div className={styles.pathGrid}>
            <p className={styles.pathsLabel}>Output Paths</p>
            {selectedFormats.map((fmt) => (
              <Field key={fmt} label={FORMAT_LABELS[fmt]} required>
                {(p) => (
                  <Input
                    {...p}
                    inputSize="sm"
                    placeholder={`/output/${fmt}/`}
                    value={paths[fmt] ?? ''}
                    onChange={(e) => onPathChange(fmt, e.target.value)}
                  />
                )}
              </Field>
            ))}
          </div>
        )}
      </Card>
    </Section>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function VodWatchFolderCreate({ serviceId }: { serviceId: string }) {
  const {
    form,
    currentStorages,
    currentUploadStorages,
    selectedStorage,
    selectedFormats,
    presets,
    notifications,
    verimatrixes,
    isPageLoading,
    isSubmitting,
    formError,
    navigate,
    updateSource,
    updateMd5sum,
    addAdditionalTrack,
    removeAdditionalTrack,
    togglePreset,
    isOutputSelected,
    toggleOutputCard,
    toggleCardOption,
    updateOutputOption,
    toggleHlsProtection,
    toggleDashProtection,
    updateGlobalOverride,
    updateDistributionTarget,
    updateDistributionStorage,
    updateDistributionPath,
    updateNotification,
    updateGeneral,
    handleSubmit,
  } = useWatchFolderCreateForm(serviceId);

  if (isPageLoading) {
    // Skeleton form blocks instead of a full-screen backdrop while source config loads.
    return (
      <Container size="lg">
        <PageHeader
          icon={<FolderOpen size={22} />}
          title="Create Watch Folder"
          description="Automate transcoding by continuously monitoring a remote directory for new video files."
        />
        <div className={styles.skeletonGrid}>
          <Skeleton variant="rect" height={160} />
          <Skeleton variant="rect" height={160} />
          <Skeleton variant="rect" height={120} />
        </div>
      </Container>
    );
  }

  return (
    <Container size="lg">
      <PageHeader
        icon={<FolderOpen size={22} />}
        title="Create Watch Folder"
        description="Automate transcoding by continuously monitoring a remote directory for new video files."
      />
      <form className={styles.page} onSubmit={handleSubmit}>

        {/* General */}
        <Section title="General" style={{ marginBottom: 0 }}>
          <Card flat className={styles.card}>
            <Field label="Name" required>
              {(p) => (
                <Input
                  {...p}
                  placeholder="e.g. Ingest FTP Archive"
                  value={form.name}
                  onChange={(e) => updateGeneral('name', e.target.value)}
                />
              )}
            </Field>

            <div className={styles.checkGrid}>
              <label className={styles.checkRow}>
                <Checkbox
                  checked={form.strict}
                  onCheckedChange={(v) => updateGeneral('strict', v === true)}
                />
                <span>
                  <span className={styles.checkLabel}>Strict mode</span>
                  <span className={styles.checkDesc}>Reject files that fail ingestion checks.</span>
                </span>
              </label>
            </div>
          </Card>
        </Section>

        {/* Source (reused from job-create) */}
        <JobCreateSourceSection
          form={{ ...form, md5sum: '' }}
          currentStorages={currentStorages}
          selectedStorage={selectedStorage}
          updateSource={updateSource}
          updateMd5sum={updateMd5sum}
          addAdditionalTrack={addAdditionalTrack}
          removeAdditionalTrack={removeAdditionalTrack}
        />

        {/* Output Formats (reused from job-create) */}
        <JobCreateOutputSection
          form={{ ...form, md5sum: '' }}
          presets={presets}
          verimatrixes={verimatrixes}
          togglePreset={togglePreset}
          isOutputSelected={isOutputSelected}
          toggleOutputCard={toggleOutputCard}
          toggleCardOption={toggleCardOption}
          updateOutputOption={updateOutputOption}
          updateGlobalOverride={updateGlobalOverride}
        />

        {/* Watch-folder-specific protection extensions (FairPlay + DASH types) */}
        <WatchFolderOutputExtension
          form={form}
          verimatrixes={verimatrixes}
          isOutputSelected={isOutputSelected}
          toggleHlsProtection={toggleHlsProtection}
          toggleDashProtection={toggleDashProtection}
        />

        {/* Distribution (watch-folder specific) */}
        <WatchFolderDistributionSection
          target={form.distribution.target}
          storage={form.distribution.storage}
          paths={form.distribution.paths}
          selectedFormats={selectedFormats}
          uploadStorages={currentUploadStorages}
          onTargetChange={updateDistributionTarget}
          onStorageChange={updateDistributionStorage}
          onPathChange={updateDistributionPath}
        />

        {/* Notification (reused from job-create) */}
        <JobCreateNotificationSection
          form={{ ...form, md5sum: '' }}
          notifications={notifications}
          updateNotification={updateNotification}
        />

        {formError && <div className={styles.formError}>{formError}</div>}

        <ActionBar
          start={
            <Button
              variant="secondary"
              type="button"
              disabled={isSubmitting}
              onClick={() => navigate(`/services/vod/${serviceId}/watch-folders`)}
            >
              Cancel
            </Button>
          }
          end={
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating…' : 'Create Watch Folder'}
            </Button>
          }
        />

      </form>
    </Container>
  );
}
